import React, { useState } from 'react';
import type { Field } from '../types';
import {
  getFieldValue,
  formatFieldValue,
  getFieldContainerClassNames,
  getFieldLabelClassNames,
  getFieldValueClassNames,
  getFieldTooltip,
} from '../utils';
import { JsonRenderer } from './JsonRenderer';
import { ValueRenderer } from './ValueRenderer';

import { useTranslation } from 'react-i18next';

interface FieldRendererProps {
  field: Field;
  data: Record<string, unknown>;
  layout?: 'row' | 'column';
  showLabels?: boolean;
  showEmptyFields?: boolean;
}

export const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  data,
  layout = 'row',
  showLabels = true,
  showEmptyFields = false,
}) => {
  const { t } = useTranslation('pleri/chat');
  const [isExpanded, setIsExpanded] = useState(false);
  const value = getFieldValue(data, field.field);
  const tooltip = getFieldTooltip(field, value);

  if (!showEmptyFields && !value) {
    return null;
  }

  // Special handling for JSON fields - use vertical layout
  const isJsonField =
    field.type === 'json' ||
    field.type === 'list' ||
    (typeof value === 'object' && value !== null && !Array.isArray(value));
  const containerClassName = isJsonField
    ? `flex flex-col space-y-2 w-full ${getFieldContainerClassNames(field, 'column')}`
    : `grid grid-cols-[180px_1fr] gap-4 items-baseline ${getFieldContainerClassNames(field, layout)}`;

  const labelClassName = isJsonField
    ? `text-sm font-medium text-gray-600 dark:text-gray-400 mb-1 ${getFieldLabelClassNames(field)}`
    : `text-sm font-medium text-gray-600 dark:text-gray-400 ${getFieldLabelClassNames(field)}`;
  const valueClassName = `text-sm text-gray-900 dark:text-gray-100 min-w-0 ${getFieldValueClassNames(field)}`;

  const renderValue = () => {
    if (isJsonField) {
      return <JsonRenderer data={value} />;
    }

    if (field.type === 'list' || Array.isArray(value)) {
      return <JsonRenderer data={value} />;
    }

    if (field.isExpandable && typeof value === 'string' && value.length > 100) {
      return (
        <div className="relative">
          <div className={`${!isExpanded ? 'max-h-24 overflow-hidden' : ''}`}>
            <ValueRenderer
              value={value}
              label={field.label}
              type={field.type}
            />
          </div>
          {!isExpanded && value.length > 100 && (
            <div className="absolute right-0 bottom-0 left-0 h-8 bg-gradient-to-t from-white to-transparent dark:from-gray-900" />
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="mt-1 text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            {isExpanded ? t('showLess') : t('showMoreLabel')}
          </button>
        </div>
      );
    }

    return (
      <ValueRenderer
        value={value as string | number | boolean | null | undefined}
        label={field.label}
        type={field.type}
      />
    );
  };

  const renderField = () => {
    switch (field.type) {
      case 'status':
        return (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
              value === 'success' || value === 'active'
                ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20 dark:bg-green-950'
                : value === 'error' || value === 'failed'
                  ? 'bg-red-50 text-red-700 ring-1 ring-red-600/20 dark:bg-red-950'
                  : value === 'warning' || value === 'pending'
                    ? 'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20 dark:bg-yellow-950'
                    : value === 'info' || value === 'in_progress'
                      ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20 dark:bg-blue-950'
                      : 'bg-gray-50 text-gray-700 ring-1 ring-gray-600/20 dark:bg-gray-800 dark:text-gray-300'
            }`}
          >
            {value === 'success' || value === 'active' ? (
              <svg
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : value === 'error' || value === 'failed' ? (
              <svg
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : value === 'warning' || value === 'pending' ? (
              <svg
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            ) : value === 'info' || value === 'in_progress' ? (
              <svg
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            ) : null}
          </span>
        );

      case 'badge':
        return (
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
              field.badgeOptions?.status === 'success'
                ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20 dark:bg-green-950'
                : field.badgeOptions?.status === 'error'
                  ? 'bg-red-50 text-red-700 ring-1 ring-red-600/20 dark:bg-red-950'
                  : field.badgeOptions?.status === 'warning'
                    ? 'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20 dark:bg-yellow-950'
                    : field.badgeOptions?.status === 'info'
                      ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20 dark:bg-blue-950'
                      : 'bg-gray-50 text-gray-700 ring-1 ring-gray-600/20 dark:bg-gray-800 dark:text-gray-300'
            }`}
          >
            {field.badgeOptions?.icon && (
              <svg
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={field.badgeOptions.icon}
                />
              </svg>
            )}
            {formatFieldValue(value, field.type)}
          </span>
        );

      case 'image':
        return (
          <div className="relative overflow-hidden rounded-lg">
            <img
              src={formatFieldValue(value, field.type) as string}
              alt={field.label || ''}
              className={`h-full w-full object-cover ${field.className || ''}`}
              style={field.style}
            />
          </div>
        );

      case 'link':
        return (
          <a
            href={formatFieldValue(value, field.type) as string}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
            {field.label || formatFieldValue(value, field.type)}
          </a>
        );

      case 'code':
        return (
          <div className="group relative">
            <pre className="overflow-x-auto rounded-lg bg-gray-900 p-4">
              <code className="text-xs text-gray-100">
                {formatFieldValue(value, field.type)}
              </code>
            </pre>
            <button
              onClick={() => {
                navigator.clipboard.writeText(
                  String(formatFieldValue(value, field.type)),
                );
              }}
              className="absolute top-2 right-2 rounded bg-gray-800 p-1.5 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-gray-100 dark:text-gray-500"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </button>
          </div>
        );

      case 'date':
      case 'datetime':
        return (
          <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            {formatFieldValue(value, field.type)}
          </span>
        );

      default:
        return renderValue();
    }
  };

  return (
    <div
      className={containerClassName}
      style={field.containerStyle}
      title={tooltip}
      onClick={() => {
        if (field.isCollapsible || field.isExpandable) {
          setIsExpanded(!isExpanded);
        }
      }}
    >
      {showLabels && field.label && (
        <div className={labelClassName} style={field.labelStyle}>
          {field.label}
          {field.required && <span className="ml-1 text-red-500">*</span>}
          {field.tooltip && (
            <div className="ml-1 inline-block">
              <svg
                className="inline h-3.5 w-3.5 text-gray-400 dark:text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          )}
        </div>
      )}
      <div className={valueClassName} style={field.style}>
        {field.nestedFields ? (
          <div className="space-y-1">
            {field.nestedFields.map((nestedField, index) => (
              <FieldRenderer
                key={index}
                field={nestedField}
                data={
                  typeof value === 'object' && value !== null
                    ? (value as Record<string, unknown>)
                    : {}
                }
                layout={layout}
                showLabels={showLabels}
                showEmptyFields={showEmptyFields}
              />
            ))}
          </div>
        ) : (
          renderField()
        )}
      </div>
    </div>
  );
};
